<script setup>
const props = defineProps({
  products: {
    type: Array,
    required: true,
  },

  filteredProducts: {
    type: Array,
    required: true,
  },
})

const emit = defineEmits(['update:filteredProducts'])

const categoryFilter = ref(null)
const colorFilter = ref(null)

const availableCategories = computed(() => {
  return props.products.map((product) => { return { id: product.product_category_id, title: product.product_category_title } }).filter((x, i, a) => a.findIndex(y => y.id === x.id) === i)
})

const availableColors = computed(() => {
  return props.products.map(product => product.variants.map(variant => variant.color)).flat().filter(n => n).filter((x, i, a) => a.findIndex(y => y.code === x.code) === i).sort((a, b) => a.code.localeCompare(b.code))
})

const activeFilters = ref([])

const filteredProducts = computed(() => {
  let filteredProducts = colorFilter.value
    ? props.products.filter(product =>
      product.variants.find(variant => (variant.color?.title === colorFilter.value.title && variant.color?.code === colorFilter.value.code)),
    )
    : props.products

  filteredProducts = categoryFilter.value
    ? filteredProducts.filter(product => product.product_category_id === categoryFilter.value.id)
    : filteredProducts

  if (!activeFilters.value.length)
    return filteredProducts

  const result = filteredProducts.filter((product) => {
    return activeFilters.value.every((filter) => {
      return product.variants.find(variant => variant[filter.dimension] === filter.measurement)
    })
  })

  return result
})

watchEffect(() => {
  emit('update:filteredProducts', filteredProducts.value)
})

const allRelevantDimensions = computed(() => {
  const dimensions = []
  props.products.forEach((product) => {
    product.relevant_dimensions.forEach((dimension) => {
      if (!dimensions.find(x => x === dimension))
        dimensions.push(dimension)
    })
  })
  return dimensions
})

function createOptionList(dimension) {
  const options = []
  props.products.forEach((product) => {
    if (product.relevant_dimensions.includes(dimension)) {
      product.variants.forEach((variant) => {
        if (variant[dimension]) {
          const measurement = variant[dimension]
          // ! Die dimension_units.default is echt een probleem, geeft vaak mm terug ook bij volume
          const desiredUnit = product.dimension_units[dimension] ?? null
          if (!options.find(x => x.measurement === measurement && x.desiredUnit === desiredUnit)) {
            options.push({
              measurement,
              desiredUnit,
            })
          }
        }
      })
    }
  })
  return options.sort((a, b) => a.measurement - b.measurement)
}

function addFilter(data) {
  if (data.option === null) {
    const newArray = activeFilters.value.filter(x => x.dimension !== data.dimension)
    activeFilters.value = newArray
    return
  }
  const newArray = activeFilters.value.filter(x => x.dimension !== data.dimension)
  newArray.push({
    dimension: data.dimension,
    measurement: data.option.measurement,
    desiredUnit: data.option.desiredUnit,
  })
  activeFilters.value = newArray
}
</script>

<template>
  <div class="w-full">
    <div v-auto-animate class="flex gap-4 flex-col w-full z-20 left-0">
      <ProductsCategoryFilter
        key="category"
        v-model="categoryFilter"
        :options="availableCategories"
        :products="products"
      />

      <ProductsColorFilter
        key="color"
        v-model="colorFilter"
        :options="availableColors"
        :products="products"
      />

      <template v-for="(dimension) in allRelevantDimensions" :key="dimension">
        <ProductFilterDimension
          :products="props.products"
          :options="createOptionList(dimension)"
          :label="$t(`dimensions.${dimension}.long`)"
          :dimension="dimension"
          class="mb-auto"
          @select="(option) => addFilter({ dimension, option })"
        />
      </template>
    </div>
  </div>
</template>
